import { interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';

// Types
import type { AnimationState, AnimationConfig } from '@/types';
import { AnimationPreset } from '@/types';

// Constants
import { ANIMATION_SPRING_CONFIGS, ANIMATION_DEFAULTS } from '@/constants';

/**
 * Hook for creating staggered text animations
 * Returns scale and opacity values for multiple text elements
 *
 * @param count Number of elements to animate
 * @param config Animation configuration
 * @returns Array of animation results with scale and opacity
 *
 * @example
 * ```tsx
 * const [nameAnim, titleAnim] = useTextAnimations(2, { stagger: 15 });
 * <h1 style={{ transform: `scale(${nameAnim.scale})`, opacity: nameAnim.opacity }}>
 * ```
 */
export const useTextAnimations = (
  count: number,
  config: AnimationConfig = {},
): AnimationState[] => {
  const {
    startFrame = ANIMATION_DEFAULTS.startFrame,
    stagger = ANIMATION_DEFAULTS.stagger,
    springConfig = AnimationPreset.DEFAULT,
  } = config;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return Array.from({ length: count }, (_, index) => {
    const delay = startFrame + index * stagger;

    const scale = spring({
      frame: frame - delay,
      fps,
      config: ANIMATION_SPRING_CONFIGS[springConfig],
    });

    const opacity = interpolate(frame, [delay, delay + stagger], [0, 1], {
      extrapolateRight: 'clamp',
    });

    return { scale, opacity };
  });
};
